import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import cn from 'classnames';

import { DEFAULT_ERROR_PROPS } from 'consts';
import { useDetectLocation } from 'hooks/common/useDetectLocation';

import AutomaticReconnectionMessage from './components/AutomaticReconnectionMessage/AutomaticReconnectionMessage';
import CheckConnectionMessage from './components/CheckConnectionMessage/CheckConnectionMessage';
import ErrorCodeInfo from './components/ErrorCodeInfo/ErrorCodeInfo';
import ErrorDescription from './components/ErrorDescription/ErrorDescription';
import ErrorTitle from './components/ErrorTitle/ErrorTitle';
import ExtendedErrorModalButton from './components/ExtendedErrorModalButton/ExtendedErrorModalButton';
import GoBackErrorButton from './components/GoBackErrorButton/GoBackErrorButton';
import RetryErrorButton from './components/RetryErrorButton/RetryErrorButton';
import { useAutoRetry } from './handlers/useAutoRetry/useAutoRetry';
import { useHandleRetry } from './handlers/useHandleRetry/useHandleRetry';
import { useResolveBack } from './handlers/useResolveBack/useResolveBack';
import { useCleanUpErrorOnUrlChange } from './hooks/useCleanUpErrorOnUrlChange/useCleanUpErrorOnUrlChange';
import { ContextType, ErrorContextPropsValue } from './types';

import styles from './error.module.scss';

const ErrorScreenContext = createContext<ContextType>({
  setError: () => {},
  clearError: () => {},
  stopLoadingError: () => {},
  autoRetry: () => {},
});

interface Props {
  children: React.ReactNode;
  apiErrorRetrying?: boolean;
}

const ErrorScreenProvider = ({ children, apiErrorRetrying }: Props) => {
  const { isPlayerLocation } = useDetectLocation();
  const [errorValue, setErrorValue] = useState<ErrorContextPropsValue>(DEFAULT_ERROR_PROPS);
  const [isPendingRetry, setIsPendingRetry] = useState(false);
  const [isPendingBack, setIsPendingBack] = useState(false);

  const setError = useCallback(
    ({
      goBackButton,
      retryButton,
      error,
      onClear,
      restartRetryConnection,
      isErrorFromTvpil,
      isRhinoError,
      playerErrorPayload,
      checkConnectionTimer,
      autoRetryTimer,
      timerInterval,
    }: ErrorContextPropsValue) => {
      setErrorValue(() => ({
        goBackButton,
        retryButton,
        error,
        onClear,
        restartRetryConnection,
        isErrorFromTvpil,
        isRhinoError,
        playerErrorPayload,
        checkConnectionTimer,
        autoRetryTimer,
        timerInterval,
      }));
    },
    [],
  );

  const stopLoadingError = useCallback(() => {
    setIsPendingBack(false);
    setIsPendingRetry(false);
  }, []);

  const {
    error: { titleId: errorTitleId, messageId, code, icon, titleDefault, messageDefault },
    retryButton: { onRetry, translationId: retryTranslationId, defaultMessage: retryDefaultMessage } = {},
    goBackButton: { goBackLink, translationId: goBackTranslationId, defaultMessage: goBackDefaultMessage } = {},
    onClear,
    restartRetryConnection,
    isErrorFromTvpil,
    isRhinoError,
    checkConnectionTimer,
    autoRetryTimer,
    timerInterval,
  } = errorValue;

  const clearError = useCallback(() => {
    if (errorValue.timerInterval) {
      clearInterval(timerInterval);
    }

    setErrorValue(DEFAULT_ERROR_PROPS);
    setIsPendingBack(false);
    setIsPendingRetry(false);
  }, [errorValue.timerInterval]);

  // TODO: resolve problem with globalStorage.isPlayerError / isPlayerLocation()
  const shouldShowChildren = (!!errorTitleId && isPlayerLocation(window.location.pathname)) || !errorTitleId;
  const isRetryInPending = isPendingRetry || !!apiErrorRetrying;
  const isDisabled = isRetryInPending || isPendingBack;

  useCleanUpErrorOnUrlChange({ isError: !!errorTitleId, clearError, onClear });

  const resolveBack = useResolveBack({ clearError, goBackLink, setIsPendingBack });

  /**
   * Retry after timer expires
   */
  const autoRetry = useAutoRetry({ setIsPendingRetry, clearError });

  /**
   * Retry by user click by "Try Again" button
   */
  const handleRetry = useHandleRetry({
    restartRetryConnection,
    setIsPendingRetry,
    clearError,
    onRetry,
    isErrorFromTvpil,
    isRhinoError,
    autoRetryTimer,
    timerInterval,
  });

  const contextValue = useMemo(() => {
    return { setError, clearError, stopLoadingError, autoRetry };
  }, [setError, clearError, stopLoadingError, autoRetry]);

  return (
    <ErrorScreenContext.Provider value={contextValue}>
      {!!errorTitleId && (
        <div
          className={cn(styles.wrapper, {
            [styles.banContent]: icon === 'banContent',
            [styles.noConnection]: icon === 'noConnection',
            [styles.hasConnection]: icon === 'hasConnection',
          })}
        >
          <ErrorTitle titleId={errorTitleId} titleDefault={titleDefault} />
          <ErrorDescription messageId={messageId} messageDefault={messageDefault} />
          <RetryErrorButton
            isPending={isRetryInPending}
            isDisabled={isDisabled}
            retryDefaultMessage={retryDefaultMessage}
            retryTranslationId={retryTranslationId}
            handleRetry={handleRetry}
            checkConnectionTimer={checkConnectionTimer}
          />
          <GoBackErrorButton
            isPendingBack={isPendingBack}
            isDisabled={isDisabled}
            goBackDefaultMessage={goBackDefaultMessage}
            goBackTranslationId={goBackTranslationId}
            checkConnectionTimer={checkConnectionTimer}
            resolveBack={resolveBack}
          />
          <ExtendedErrorModalButton error={errorValue} />
          <CheckConnectionMessage timerSec={checkConnectionTimer} />
          <AutomaticReconnectionMessage timerSec={autoRetryTimer} isRetryInPending={isRetryInPending} />
          {!!code && <ErrorCodeInfo code={code} autoRetryTimer={autoRetryTimer} />}
        </div>
      )}
      {shouldShowChildren && children}
    </ErrorScreenContext.Provider>
  );
};

const useErrorScreenContext = () => useContext(ErrorScreenContext);

export { ErrorScreenContext, ErrorScreenProvider, useErrorScreenContext };
